/**
 * Principal Dashboard
 * 
 * Displays all teachers, all students, school-wide analytics, and teacher
 * activity metrics for their school only. Cannot see other schools' data.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import type { StudentScore } from '../../types/lms';
import { Link } from 'react-router-dom';
import { FaSchool, FaChalkboardTeacher, FaArrowRight, FaUserCheck, FaBell, FaClipboardList, FaFileAlt } from 'react-icons/fa';
import { learnXRFontStyle, TrademarkSymbol } from '../../Components/LearnXRTypography';
import { Card, CardContent } from '../../Components/ui/card';
import { Button } from '../../Components/ui/button';
import { Badge } from '../../Components/ui/badge';
// The same panels the teacher sees, over every class in the school. Shared so a
// principal and their teachers cannot be shown two different averages for one
// class.
import { SchoolPerformanceSection } from '../../Components/analytics/SchoolPerformanceSection';

const PrincipalDashboard = () => {
  const { user, profile } = useAuth();
  const [teachers, setTeachers] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [pendingStudents, setPendingStudents] = useState<any[]>([]);
  const [pendingTeachers, setPendingTeachers] = useState<any[]>([]);
  const [scores, setScores] = useState<StudentScore[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  /*
    Why a query failed, when one does.

    These subscriptions had no error callback at all, and both of them needed a
    composite index that did not exist — an equality filter with an orderBy on
    another field always does. Firestore rejected them outright and the rejection
    was swallowed, so this page showed zeros indefinitely with nothing anywhere
    to say the read had failed. An error must never render as an absence of
    results.
  */
  const [dataError, setDataError] = useState<string | null>(null);
  /*
    The profile fields this screen depends on.

    Depending on the whole `profile` object re-subscribed every query here
    whenever anything touched the user document, because Firestore rebuilds that
    object on each snapshot.
  */
  const profileRole = profile?.role;
  const managedSchoolId = profile?.managed_school_id;
  const [loading, setLoading] = useState(true);
  /*
    True once the first snapshot has landed.

    The page used to render a full-screen spinner whenever `loading` was true,
    and every re-subscribe set it true again — so a write to the user document
    replaced a correct, populated dashboard with a spinner. After the first load
    there is always something worth showing, and live updates land in place.
  */
  const [hasLoaded, setHasLoaded] = useState(false);

  // Latches on the first completed load and never unlatches, so a later refresh
  // can never put the page back behind a spinner.
  useEffect(() => {
    if (!loading) setHasLoaded(true);
  }, [loading]);


  // Fetch pending students for approval count
  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'principal' || !profile.managed_school_id) return;

    const schoolId = profile.managed_school_id;

    // Fetch all pending students in the school
    const pendingStudentsQuery = query(
      collection(db, 'users'),
      where('role', '==', 'student'),
      where('school_id', '==', schoolId),
      where('approvalStatus', '==', 'pending')
    );

    const unsubscribePendingStudents = onSnapshot(pendingStudentsQuery, (snapshot) => {
      const pendingData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      
      console.log('🔍 PrincipalDashboard: Pending students', {
        total: pendingData.length,
        schoolId,
      });
      
      setPendingStudents(pendingData);
    }, (error) => {
      console.error('Error fetching pending students:', error);
    });

    // Fetch all pending teachers in the school
    const pendingTeachersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'teacher'),
      where('school_id', '==', schoolId),
      where('approvalStatus', '==', 'pending')
    );

    const unsubscribePendingTeachers = onSnapshot(pendingTeachersQuery, (snapshot) => {
      const pendingData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      
      console.log('🔍 PrincipalDashboard: Pending teachers', {
        total: pendingData.length,
        schoolId,
      });
      
      setPendingTeachers(pendingData);
    }, (error) => {
      console.error('Error fetching pending teachers:', error);
    });

    return () => {
      unsubscribePendingStudents();
      unsubscribePendingTeachers();
    };
  }, [user?.uid, profileRole, managedSchoolId]);

  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'principal' || !profile.managed_school_id) return;

    setLoading(true);
    const schoolId = profile.managed_school_id;

    // Get all teachers in school (both approved and pending)
    const teachersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'teacher'),
      where('school_id', '==', schoolId)
    );

    const unsubscribeTeachers = onSnapshot(teachersQuery, (snapshot) => {
      const teachersData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      setTeachers(teachersData);
    });

    // Get all students in school (both approved and pending)
    const studentsQuery = query(
      collection(db, 'users'),
      where('role', '==', 'student'),
      where('school_id', '==', schoolId)
    );

    const unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
      const studentsData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      setStudents(studentsData);
    });

    // Get all scores in school
    const scoresQuery = query(
      collection(db, 'student_scores'),
      where('school_id', '==', schoolId),
      orderBy('completed_at', 'desc')
    );

    const unsubscribeScores = onSnapshot(scoresQuery, (snapshot) => {
      const scoresData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as StudentScore[];
      setScores(scoresData);
      setDataError(null);
      setLoading(false);
    }, (error) => {
      console.error('PrincipalDashboard: student_scores query failed', error);
      setDataError(`Student scores could not be read: ${error.message}`);
      setLoading(false);
    });

    // Classes in the school, so per-class figures can be computed the same way
    // the teacher's dashboard computes them.
    const classesQuery = query(collection(db, 'classes'), where('school_id', '==', schoolId));
    const unsubscribeClasses = onSnapshot(classesQuery, (snapshot) => {
      setClasses(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error('PrincipalDashboard: classes query failed', error);
      setDataError(`Classes could not be read: ${error.message}`);
    });

    return () => {
      unsubscribeTeachers();
      unsubscribeStudents();
      unsubscribeScores();
      unsubscribeClasses();
    };
  }, [user?.uid, profileRole, managedSchoolId]);

  // Only before anything has ever been shown. A refresh over existing
  // content must not blank the page.
  if (loading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary/30 border-t-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading school dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 pb-6 border-b border-border">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 border border-border flex items-center justify-center">
                <FaSchool className="text-primary text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-1" style={learnXRFontStyle}>
                  <span className="text-foreground">In</span>
                  <span className="text-primary">3D.ai</span>
                  <TrademarkSymbol />
                </h1>
                <h2 className="text-xl font-semibold text-foreground">School Dashboard</h2>
                <p className="text-muted-foreground text-sm mt-0.5">School-wide analytics and management</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick action tiles */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          <Link
            to="/question-paper/generate"
            className="rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaClipboardList className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">AI Question Paper</div>
                <div className="text-xs text-muted-foreground truncate">Generate CBSE/RBSE papers</div>
              </div>
            </div>
          </Link>
          <Link
            to="/question-paper/library"
            className="rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaFileAlt className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">Paper Library</div>
                <div className="text-xs text-muted-foreground truncate">Saved question papers</div>
              </div>
            </div>
          </Link>
          <Link
            to="/admin/classes?tab=teachers"
            className="rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaUserCheck className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">Teacher Approvals</div>
                <div className="text-xs text-muted-foreground truncate">Review pending teachers</div>
              </div>
            </div>
          </Link>
        </section>

        {/* Approval Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className={pendingStudents.length > 0 ? 'border-amber-500/40' : ''}>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 border border-border flex items-center justify-center relative">
                    <FaUserCheck className="text-primary text-2xl" />
                    {pendingStudents.length > 0 && (
                      <Badge className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-destructive text-destructive-foreground">
                        {pendingStudents.length}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-foreground">Student Approvals</h2>
                      {pendingStudents.length > 0 && (
                        <Badge variant="secondary" className="gap-1">
                          <FaBell className="text-xs" />
                          {pendingStudents.length} pending
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm mt-1">
                      {pendingStudents.length > 0
                        ? `${pendingStudents.length} student(s) waiting for approval.`
                        : 'Review and approve students joining your school.'}
                    </p>
                  </div>
                </div>
                <Button asChild variant={pendingStudents.length > 0 ? 'default' : 'secondary'} className="gap-2 shrink-0">
                  <Link to="/admin/classes?tab=students&filter=pending">
                    {pendingStudents.length > 0 ? 'Review Students' : 'Student Approvals'}
                    <FaArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card className={pendingTeachers.length > 0 ? 'border-amber-500/40' : ''}>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 border border-border flex items-center justify-center relative">
                    <FaChalkboardTeacher className="text-primary text-2xl" />
                    {pendingTeachers.length > 0 && (
                      <Badge className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-destructive text-destructive-foreground">
                        {pendingTeachers.length}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-foreground">Teacher Approvals</h2>
                      {pendingTeachers.length > 0 && (
                        <Badge variant="secondary" className="gap-1">
                          <FaBell className="text-xs" />
                          {pendingTeachers.length} pending
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm mt-1">
                      {pendingTeachers.length > 0
                        ? `${pendingTeachers.length} teacher(s) waiting for approval.`
                        : 'Review and approve teachers joining your school.'}
                    </p>
                  </div>
                </div>
                <Button asChild variant={pendingTeachers.length > 0 ? 'default' : 'secondary'} className="gap-2 shrink-0">
                  <Link to="/admin/classes?tab=teachers">
                    {pendingTeachers.length > 0 ? 'Review Teachers' : 'Teacher Approvals'}
                    <FaArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Teacher Management - quick access for Principal */}
        <div className="mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                    <FaChalkboardTeacher className="text-primary text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Teacher Management</h2>
                    <p className="text-muted-foreground text-sm mt-1">
                      View and manage teachers, assign classes, and oversee school staff.
                    </p>
                  </div>
                </div>
                <Button asChild variant="secondary" className="gap-2 shrink-0">
                  <Link to="/admin/classes">
                    Open Class Management
                    <FaArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Teachers Overview */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Teachers ({teachers.length})</h2>
          {teachers.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <FaChalkboardTeacher className="text-4xl text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No teachers in your school yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teachers.map((teacher) => {
                const teacherClasses = teacher.managed_class_ids?.length || 0;
                const teacherStudents = students.filter(s => 
                  s.class_ids?.some(classId => teacher.managed_class_ids?.includes(classId))
                ).length;

                return (
                  <div
                    key={teacher.uid}
                    className="rounded-xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-foreground font-medium">
                        {teacher.name || teacher.displayName || 'Unknown Teacher'}
                      </h3>
                      {teacher.approvalStatus && (
                        <Badge variant={teacher.approvalStatus === 'approved' ? 'default' : teacher.approvalStatus === 'pending' ? 'secondary' : 'destructive'} className="text-xs">
                          {teacher.approvalStatus}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm mb-3">{teacher.email}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {teacherClasses} classes
                      </span>
                      <span className="text-primary">
                        {teacherStudents} students
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* School Academic Performance */}
        {managedSchoolId && (
          <SchoolPerformanceSection
            schoolId={managedSchoolId}
            classes={classes}
            students={students}
            teachers={teachers}
            scores={scores}
            loading={loading}
            error={dataError}
            canEditThreshold
          />
        )}
      </div>
    </div>
  );
};

export default PrincipalDashboard;
